import { LabelingTypeEnum } from 'src/types/tags';
import { DisplayConfig } from 'src/types/readings/readings';

const MAX_LABELLED_VALUES = 20;

const MIN_TICK_SEPARATION_RATIO = 1 / 25;

const isLabelledMode = (display?: DisplayConfig | null): boolean =>
  !!display &&
  (display.mode === LabelingTypeEnum.LABEL || display.mode === LabelingTypeEnum.VALUE_LABEL);

const getLabelsByValue = (display: DisplayConfig): Map<number, string> =>
  new Map(
    Object.entries(display.labels ?? {})
      .map(([key, label]): [number, string] => [Number(key), label])
      .filter(([value]) => !Number.isNaN(value))
  );

const resolveLabel = (value: number, display: DisplayConfig): string => {
  if (!Number.isFinite(value)) return '';

  return getLabelsByValue(display).get(value) ?? display.defaultLabel ?? '';
};

export const formatValueLabel = (value: number, display?: DisplayConfig | null): string => {
  if (!display) return String(value);

  const label = resolveLabel(value, display);

  switch (display.mode) {
    case LabelingTypeEnum.LABEL:
      return label || String(value);

    case LabelingTypeEnum.VALUE_LABEL:
      return label ? `${value} - ${label}` : String(value);

    default:
      return String(value);
  }
};

// The values the tag has an option configured for, smallest first.
const getPredefinedOptionValues = (display: DisplayConfig): number[] =>
  [...getLabelsByValue(display).keys()].sort((a, b) => a - b);

type AxisTick = { value: number; label: string };

type ValueRun = { lowest: number; highest: number };

const getUndefinedRuns = (series: number[], labelsByValue: Map<number, string>): ValueRun[] => {
  const runs: ValueRun[] = [];
  let isInRun = false;

  series.forEach((value) => {
    // A gap in the data is not a reading either way, so it neither starts nor ends a run.
    if (!Number.isFinite(value)) return;

    if (labelsByValue.has(value)) {
      isInRun = false;
      return;
    }

    if (isInRun) {
      const current = runs[runs.length - 1];
      current.lowest = Math.min(current.lowest, value);
      current.highest = Math.max(current.highest, value);
      return;
    }

    runs.push({ lowest: value, highest: value });
    isInRun = true;
  });

  return runs;
};

const buildLabelledTicks = (display: DisplayConfig, dataSeries: number[][]): AxisTick[] => {
  const definedValues = getPredefinedOptionValues(display);

  if (definedValues.length > MAX_LABELLED_VALUES) return [];

  const labelsByValue = getLabelsByValue(display);

  const runValues = [
    ...new Set(
      dataSeries
        .flatMap((series) => getUndefinedRuns(series, labelsByValue))
        .flatMap((run) => [run.lowest, run.highest])
    ),
  ].sort((a, b) => a - b);

  const candidates = [...definedValues, ...runValues];
  const span = candidates.length ? Math.max(...candidates) - Math.min(...candidates) : 0;
  const minimumGap = span * MIN_TICK_SEPARATION_RATIO;

  const tickValues = runValues
    .reduce<number[]>(
      (kept, value) =>
        kept.some((existing) => Math.abs(existing - value) < minimumGap) ? kept : [...kept, value],
      definedValues
    )
    .sort((a, b) => a - b);

  if (!tickValues.length) return [];

  return tickValues.map((value) => ({
    value,
    label: formatValueLabel(value, display),
  }));
};

const buildYAxis = (
  tag: any,
  display?: DisplayConfig | null,
  dataSeries?: number[][],
  isOnlyAxis = false
): Record<string, any> => {
  const series = dataSeries ?? [];
  // Runs need the rows kept apart and in order; the range only needs every reading.
  const readings = series.flat();
  const labelledTicks = isLabelledMode(display) ? buildLabelledTicks(display!, series) : [];

  const axis: Record<string, any> = {
    type: 'linear',
    display: true,
    position: 'left',
    ticks: {
      color: tag?.color,
    },
    grid: {
      drawOnChartArea: false,
    },
  };

  const hasExplicitRange = !tag?.yAxisAuto && tag?.yAxisFrom != null && tag?.yAxisTo != null;

  if (hasExplicitRange) {
    axis.min = tag.yAxisFrom;
    axis.max = tag.yAxisTo;
  }

  // Nothing to label: the axis keeps Chart.js' own numeric ticks, which is also what the
  // "Value" display option asks for.
  if (!labelledTicks.length) return axis;

  if (!hasExplicitRange) {
    axis.min = readings.reduce((lowest, value) => Math.min(lowest, value), labelledTicks[0].value);
    axis.max = readings.reduce(
      (highest, value) => Math.max(highest, value),
      labelledTicks[labelledTicks.length - 1].value
    );

    // A single option with no reading beyond it would collapse the scale.
    if (axis.min === axis.max) {
      axis.min -= 1;
      axis.max += 1;
    }
  }

  const visibleTicks = labelledTicks.filter(
    (tick) => tick.value >= axis.min && tick.value <= axis.max
  );

  if (!visibleTicks.length) return axis;

  const labelByValue = new Map(visibleTicks.map((tick) => [tick.value, tick.label]));

  axis.afterBuildTicks = (scale: any) => {
    scale.ticks = visibleTicks.map((tick) => ({ value: tick.value }));
  };
  axis.ticks.autoSkip = false;
  axis.ticks.callback = (value: number | string) => labelByValue.get(Number(value)) ?? '';

  if (isOnlyAxis) {
    axis.grid = { drawOnChartArea: true, color: '#e0e0e0' };
  }

  return axis;
};

// dataSeriesByTagIndex holds one ordered array of readings per response row, not one flat set
// of values per tag: the undefined runs are read off the order.
export const generateYAxesWithColors = (
  tags: any[],
  displayByTagIndex: Record<number, DisplayConfig | null> = {},
  dataSeriesByTagIndex: Record<number, number[][]> = {}
) => {
  if (tags.length === 0) return {};

  const yAxes: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/dot-notation
  yAxes['y'] = buildYAxis(
    tags[0],
    displayByTagIndex[0],
    dataSeriesByTagIndex[0],
    tags.length === 1
  );

  tags.slice(1).forEach((tag, index) => {
    // tags[1] renders on axis 'y0', tags[2] on 'y1', ... so the tag index is index + 1.
    const tagIndex = index + 1;
    yAxes[`y${index}`] = buildYAxis(
      tag,
      displayByTagIndex[tagIndex],
      dataSeriesByTagIndex[tagIndex]
    );
  });

  return yAxes;
};
